import { useState, useEffect, useCallback, createContext, useContext } from 'react'
import { supabase } from '../lib/supabase'
import { trackActivity } from '../lib/api'
import { queryClient } from '../main.jsx'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [role, setRole] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const resolveRole = useCallback((supabaseUser) => {
    if (!supabaseUser) return null
    // TRANSITION: prefer app_metadata.role (server-set, client-immutable) over
    // user_metadata.role (client-writable, legacy). Mirrors the backend
    // resolution order so a user with roles in both places sees the same
    // effective role on the frontend and the API. Fall through to 'client'
    // for legacy users whose role hasn't been backfilled yet.
    return (
      supabaseUser.app_metadata?.role ||
      supabaseUser.user_metadata?.role ||
      'client'
    )
  }, [])

  useEffect(() => {
    let mounted = true

    async function initSession() {
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession()
        if (sessionError) throw sessionError

        if (mounted) {
          if (session?.user) {
            setUser(session.user)
            setRole(resolveRole(session.user))
          } else {
            setUser(null)
            setRole(null)
          }
          setLoading(false)
        }
      } catch (err) {
        if (mounted) {
          console.error('Session init error:', err)
          setLoading(false)
        }
      }
    }

    initSession()

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        if (!mounted) return
        if (session?.user) {
          setUser(session.user)
          const resolvedRole = resolveRole(session.user)
          setRole(resolvedRole)
          // Log the sign-in for the admin activity view. Consolidated here
          // (rather than in login()) so BOTH password and magic-link
          // sign-ins get logged from one place. Client-role only — admin
          // logins aren't audited.
          //
          // The listener also fires on TOKEN_REFRESHED and initial session
          // hydration, which would double-count. Dedupe with sessionStorage:
          // one flag per tab-session. sessionStorage is auto-cleared when
          // the tab closes, so a fresh tab always fires once, refreshes
          // never re-fire, and closing/reopening the tab correctly logs
          // the new sign-in. logout() clears the flag so the next sign-in
          // in the same tab re-fires.
          if (_event === 'SIGNED_IN' && resolvedRole === 'client') {
            if (sessionStorage.getItem('activity_login_tracked') !== '1') {
              sessionStorage.setItem('activity_login_tracked', '1')
              trackActivity({ event_type: 'login' })
            }
          }
        } else {
          setUser(null)
          setRole(null)
        }
        setLoading(false)
      }
    )

    return () => {
      mounted = false
      subscription.unsubscribe()
    }
  }, [resolveRole])

  const login = useCallback(async (email, password) => {
    setError(null)
    setLoading(true)
    try {
      const { data, error: authError } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password,
      })
      if (authError) throw authError
      // Sign-in logging happens in onAuthStateChange's SIGNED_IN branch so
      // magic-link sign-ins (which never hit this function) are also logged.
      return data
    } catch (err) {
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  const logout = useCallback(async () => {
    setLoading(true)
    try {
      // Log BEFORE signOut so the request still has an authenticated
      // Bearer token — signOut clears the session and the request
      // interceptor would send no auth header, causing a silent 401.
      // Client-role only, same rationale as login.
      if (role === 'client') {
        await trackActivity({ event_type: 'logout' })
      }
      // Clear the per-tab login-tracked flag so the NEXT sign-in in this
      // same tab re-fires the login event through onAuthStateChange.
      sessionStorage.removeItem('activity_login_tracked')
      // Purge per-user state BEFORE signOut so the next user (or the login
      // screen re-mount) can't briefly see the previous user's cached data.
      // - queryClient.clear(): drops every cached query/mutation.
      // - removeAllChannels(): tears down realtime subscriptions that were
      //   filtered by the outgoing user's client_id / auth JWT.
      queryClient.clear()
      await supabase.removeAllChannels()
      await supabase.auth.signOut()
      setUser(null)
      setRole(null)
    } catch (err) {
      console.error('Logout error:', err)
    } finally {
      setLoading(false)
    }
  }, [role])

  // Convenience flag: any freshly-invited / admin-created user gets
  // force_password_change = true on the backend; the SetPassword page reads
  // this to decide whether to render the "set your password" prompt or just
  // bounce the user onward. Cleared by the SetPassword page itself once they
  // save a real password.
  // TRANSITION: prefer app_metadata (server-set, client-immutable) over
  // user_metadata (client-writable, legacy). Nullish-coalesce so an explicit
  // `false` in app_metadata correctly overrides a stale `true` in
  // user_metadata during the migration window.
  const forcePasswordChange = !!(
    user?.app_metadata?.force_password_change ??
    user?.user_metadata?.force_password_change
  )

  return (
    <AuthContext.Provider value={{ user, role, loading, error, login, logout, forcePasswordChange }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    // Fallback for components rendered outside AuthProvider
    // This keeps backwards compatibility with direct hook usage
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return ctx
}
